/**
 * One Light / Dark / System choice across every internal app.
 *
 * Each app keeps its own localStorage, which is per subdomain, so a choice
 * made on tasks.ordo.com never reached rfps.ordo.com: the stale local value
 * there won on load. The shared copy is a cookie on the parent domain
 * (`.ordo.com`), which every internal app can read before first paint,
 * server-side included (the Next apps render `<html>` from it).
 *
 * Opt-in only. The customer-facing Ordo app (app.ordo.com) has its own theme
 * experience and never reads or writes this cookie; nothing here runs unless
 * an app calls it or passes `shared: true` to `useColorMode`.
 *
 * Framework-free and safe to import on the server: nothing touches `document`
 * until a function is called.
 */

export type SharedColorMode = 'light' | 'dark' | 'system'

export const SHARED_COLOR_MODE_COOKIE = 'ordo_color_mode'
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365

function isMode(value: unknown): value is SharedColorMode {
  return value === 'light' || value === 'dark' || value === 'system'
}

/**
 * The cookie's Domain: `.ordo.com` on ordo.com hosts so every subdomain shares
 * it; none elsewhere. On localhost a host-only cookie is already shared, since
 * cookies ignore the port, so every local app on localhost sees the same one.
 */
export function sharedColorModeCookieDomain(hostname: string): string | null {
  return hostname === 'ordo.com' || hostname.endsWith('.ordo.com') ? '.ordo.com' : null
}

/** Reads the shared choice out of a `document.cookie` or `Cookie:` header string. */
export function parseSharedColorMode(cookieHeader: string | null | undefined): SharedColorMode | null {
  const match = new RegExp(`(?:^|;\\s*)${SHARED_COLOR_MODE_COOKIE}=([^;]*)`).exec(cookieHeader ?? '')
  const value = match ? decodeURIComponent(match[1]) : null
  return isMode(value) ? value : null
}

/** The `document.cookie` assignment that stores `mode` for every app on this site. */
export function serializeSharedColorMode(mode: SharedColorMode, hostname: string, secure: boolean): string {
  const domain = sharedColorModeCookieDomain(hostname)
  return [
    `${SHARED_COLOR_MODE_COOKIE}=${mode}`,
    'Path=/',
    `Max-Age=${MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    domain ? `Domain=${domain}` : null,
    secure ? 'Secure' : null,
  ]
    .filter(Boolean)
    .join('; ')
}

/**
 * Which choice an internal app should show: the shared cookie (the last choice
 * made in any app on this device), then the account preference from
 * multipass, then this app's own stored value, then System. The account beats
 * the local value so a device that has not chosen since this shipped follows
 * the last choice made anywhere rather than an old per-app leftover.
 */
export function pickSharedColorMode(sources: {
  shared: SharedColorMode | null
  account?: SharedColorMode | null
  local?: string | null
}): SharedColorMode {
  if (sources.shared) {
    return sources.shared
  }
  if (sources.account) {
    return sources.account
  }
  return isMode(sources.local) ? sources.local : 'system'
}

export function readSharedColorMode(): SharedColorMode | null {
  if (typeof document === 'undefined') {
    return null
  }
  return parseSharedColorMode(document.cookie)
}

export function writeSharedColorMode(mode: SharedColorMode): void {
  if (typeof document === 'undefined') {
    return
  }
  const loc = globalThis.location
  document.cookie = serializeSharedColorMode(mode, loc?.hostname ?? '', loc?.protocol === 'https:')
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && (window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false)
}

/** Paints `<html>` for a choice: `dark` or `light`, System resolved against the OS. */
export function paintColorMode(mode: SharedColorMode): void {
  if (typeof document === 'undefined') {
    return
  }
  const dark = mode === 'dark' || (mode === 'system' && prefersDark())
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(dark ? 'dark' : 'light')
}

/**
 * An internal app's on-load sync, called once the account preference
 * (`getColorMode(user)`) is known: picks the shared choice and repaints.
 * Replaces the per-app "local value wins" default. Never writes this app's
 * localStorage, so the account default stays non-sticky; it seeds the shared
 * cookie from the account instead, which every app's pre-paint reads first.
 */
export function syncSharedColorMode(storageKey: string, account: SharedColorMode | null | undefined): SharedColorMode {
  let local: string | null = null
  try {
    local = globalThis.localStorage?.getItem(storageKey) ?? null
  } catch {
    // private mode / blocked storage
  }
  const shared = readSharedColorMode()
  const mode = pickSharedColorMode({ shared, account, local })
  if (!shared && account) {
    // Seed the shared copy from the account so the next app paints right
    // before it has even loaded the user.
    writeSharedColorMode(account)
  }
  paintColorMode(mode)
  return mode
}
